<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div class="trezo-card-content relative">
      <span class="block"> {{ directT('dashboard.crm.annualProfit') }} </span>
      <h5 class="mb-0 mt-[3px] text-[20px] pb-[15px]" v-if="loading">
        {{ directT('dashboard.crm.loading') }}
      </h5>
      <h5 class="mb-0 mt-[10px] text-[20px] pb-[15px]" v-else>
        {{ formatPrice(totalProfit, currencyCode) }}
      </h5>
      <div
        class="absolute -top-[28px] ltr:-right-[9px] rtl:-left-[9px] max-w-[120px]"
      >
        <apexchart
          v-if="isClient && monthlyProfits && monthlyProfits.length > 0"
          type="area"
          height="95"
          :options="annualProfit"
          :series="profit"
        ></apexchart>
      </div>
      <div class="mt-[15px] md:mt-[24px] flex items-center justify-end">
        <span class="block text-sm"> {{ directT('dashboard.crm.last12Months') }} </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";
import { useI18n } from "vue-i18n";
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Type definition for translation object structure
type TranslationObject = Record<string, any>;

// Store direct references to translation files
const translationFiles = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations
};

// Define interfaces for type safety
interface Deal {
  dealamount: number | null;
  dealtime: string | null;
}

export default defineComponent({
  name: "AnnualProfitChart",
  props: {
    currentUserId: {
      type: String,
      default: null
    },
    userPermissions: {
      type: Object,
      default: () => ({
        is_owner: false,
        edit_all_leads: false,
        view_all_leads: false,
        can_invite_users: false
      })
    }
  },
  setup(props) {
    const isClient = ref(false);
    const loading = ref(true);
    const totalProfit = ref(0);
    // Initialize with empty array to prevent undefined errors
    const monthlyProfits = ref<number[]>([]);
    const organizationId = ref<string | null>(null);
    const monthNames = ref(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']);
    const currencyCode = ref('ILS'); // ברירת מחדל
    
    // i18n setup
    const { locale } = useI18n();
    
    // Direct translation function that bypasses vue-i18n
    const directT = (key: string, params: Record<string, any> = {}): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      
      if (typeof result !== 'string') {
        console.warn(`Translation key ${key} does not resolve to a string`);
        return key;
      }
      
      // Handle parameter substitution if needed
      if (Object.keys(params).length > 0) {
        let finalResult: string = result;
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          finalResult = finalResult.replace(new RegExp(`{${paramKey}}`, 'g'), String(paramValue));
        });
        return finalResult;
      }
      
      return result;
    };
    
    // Format currency with appropriate symbol
    const formatPrice = (amount: number | string, currencyCode: string): string => {
      let numericAmount: number;
      
      // Convert string to number if needed
      if (typeof amount === 'string') {
        numericAmount = parseFloat(amount.replace(/[$,]/g, ''));
      } else {
        numericAmount = amount;
      }
      
      // Currency symbol mapping
      const currencySymbols: Record<string, string> = {
        'USD': '$',
        'EUR': '€',
        'GBP': '£',
        'ILS': '₪',
        'JPY': '¥',
      };
      
      const symbol = currencySymbols[currencyCode] || currencyCode;
      
      // Check if RTL currency
      const rtlCurrencies = ['ILS', 'SAR', 'AED', 'EGP', 'JOD'];
      
      if (rtlCurrencies.includes(currencyCode)) {
        // For RTL languages (Hebrew, Arabic) - symbol after amount
        return `${numericAmount.toFixed(0)} ${symbol}`;
      } else {
        // For LTR languages (English) - symbol before amount
        return `${symbol}${numericAmount.toFixed(0)}`;
      }
    };
    
    const profit = computed(() => [
      {
        name: directT('dashboard.crm.annualProfit'),
        data: monthlyProfits.value || [],
      },
    ]);
    
    const annualProfit = ref({
      chart: {
        type: "area",
        height: 95,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
      },
      colors: ["#C52B09"],
      dataLabels: {
        enabled: false,
      },
      grid: {
        show: false,
        borderColor: "#ECEEF2",
      },
      stroke: {
        curve: "monotoneCubic",
        width: 1,
      },
      xaxis: {
        categories: [] as string[],
        axisTicks: {
          show: false,
          color: "#ECEEF2",
        },
        axisBorder: {
          show: false,
          color: "#ECEEF2",
        },
        labels: {
          show: false,
          style: {
            colors: "#8695AA",
            fontSize: "12px",
          },
        },
      },
      yaxis: {
        show: false,
        labels: {
          formatter: (val: string) => {
            return formatPrice(Number(val), currencyCode.value);
          },
          style: {
            colors: "#64748B",
            fontSize: "12px",
          },
        },
        axisBorder: {
          show: false,
          color: "#ECEEF2",
        },
        axisTicks: {
          show: false,
          color: "#ECEEF2",
        },
      },
      legend: {
        show: false,
        position: "top",
        fontSize: "12px",
        horizontalAlign: "left",
        itemMargin: {
          horizontal: 8,
          vertical: 0,
        },
        labels: {
          colors: "#64748B",
        },
        markers: {
          width: 9,
          height: 9,
          offsetX: -2,
          offsetY: -0.5,
          radius: 2,
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return formatPrice(val, currencyCode.value);
          }
        }
      }
    });

    // Format amount with commas and k for thousands
    const formatAmount = (amount: number): string => {
      if (amount >= 1000) {
        return (amount / 1000).toLocaleString('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }) + 'k';
      }
      return amount.toLocaleString('en-US');
    };
    
    // Get date from X months ago in ISO format
    const getDateXMonthsAgo = (months: number): string => {
      const date = new Date();
      date.setMonth(date.getMonth() - months);
      return date.toISOString();
    };
    
    // Get the organization's currency
    const getCurrency = async () => {
      try {
        // Try to get organization's currency from database
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user) {
          return; // Default will be used
        }
  
        // Get the user's organization ID
        const { data: profileData } = await supabase
          .from('user_profiles')
          .select('org_id')
          .eq('user_id', userData.user.id)
          .single();
  
        if (!profileData?.org_id) {
          return; // Default will be used
        }
  
        // Get organization's currency
        const { data: orgData } = await supabase
          .from('organizations')
          .select('currency')
          .eq('id', profileData.org_id)
          .single();
  
        if (orgData?.currency) {
          currencyCode.value = orgData.currency as string;
        }
      } catch (error) {
        console.error('Error fetching currency:', error);
        // Default currency will be used
      }
    };
    
    // Fetch annual profit data
    const fetchAnnualProfit = async () => {
      try {
        loading.value = true;
        organizationId.value = await getCurrentOrgId();
        
        // Get organization's currency setting
        await getCurrency();
        
        if (!organizationId.value) {
          console.error("User is not associated with an organization");
          loading.value = false;
          return;
        }
        
        const twelveMonthsAgo = getDateXMonthsAgo(12);
        
        // Build query based on permissions
        let query = supabase
          .from('leads')
          .select('dealamount, dealtime')
          .eq('org_id', organizationId.value)
          .not('dealtime', 'is', null)
          .not('dealamount', 'is', null)
          .gte('dealtime', twelveMonthsAgo);
          
        // If user can't view all leads, only show assigned leads
        if (props.currentUserId && !props.userPermissions.is_owner && !props.userPermissions.view_all_leads) {
          query = query.eq('assigned_to', props.currentUserId);
        }
        
        // Execute the query
        const { data: annualDeals, error } = await query;
        
        if (error) throw error;
        
        // Calculate total amount from all deals
        let total = 0;
        if (annualDeals && annualDeals.length > 0) {
          total = (annualDeals as Deal[]).reduce((sum, deal) => {
            const amount = typeof deal.dealamount === 'number' ? deal.dealamount : 0;
            return sum + amount;
          }, 0);
        }
        totalProfit.value = total;
        
        // Initialize monthly profits with some default data
        // This ensures monthlyProfits has a value before the calculation completes
        monthlyProfits.value = new Array(12).fill(0);
        
        // Calculate monthly profits for the chart
        await calculateMonthlyProfits(twelveMonthsAgo);
        
      } catch (error) {
        console.error('Error fetching annual profit data:', error);
        // Ensure we have a fallback if there's an error
        monthlyProfits.value = new Array(12).fill(0);
      } finally {
        loading.value = false;
      }
    };
    
    // Calculate profits per month for the last 12 months
    const calculateMonthlyProfits = async (twelveMonthsAgo: string) => {
      try {
        // Build query based on permissions
        let query = supabase
          .from('leads')
          .select('dealamount, dealtime')
          .eq('org_id', organizationId.value)
          .not('dealtime', 'is', null)
          .not('dealamount', 'is', null)
          .gte('dealtime', twelveMonthsAgo);
          
        // If user can't view all leads, only show assigned leads
        if (props.currentUserId && !props.userPermissions.is_owner && !props.userPermissions.view_all_leads) {
          query = query.eq('assigned_to', props.currentUserId);
        }
        
        // Execute the query
        const { data: monthlyDeals, error } = await query;
        
        if (error) throw error;
        
        // Initialize monthly profits array (12 months)
        const monthlyProfitsArray = new Array(12).fill(0);
        
        if (monthlyDeals && monthlyDeals.length > 0) {
          // Calculate profit for each month
          (monthlyDeals as Deal[]).forEach(deal => {
            if (deal.dealtime && deal.dealamount) {
              const dealDate = new Date(deal.dealtime);
              const monthIndex = dealDate.getMonth();
              monthlyProfitsArray[monthIndex] += deal.dealamount;
            }
          });
        }
        
        // Update the ref with the calculated values
        monthlyProfits.value = monthlyProfitsArray;
        
      } catch (error) {
        console.error('Error calculating monthly profits:', error);
      }
    };
    
    onMounted(async () => {
      isClient.value = true;
      await fetchAnnualProfit();
    });
    
    return {
      isClient,
      loading,
      totalProfit,
      monthlyProfits,
      profit,
      annualProfit,
      currencyCode,
      formatPrice,
      directT,
    };
  },
});
</script>
